import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { captureTracking, getCheckoutUrl, getOrigem } from "../lib/tracking";
import { AudioPlayer } from "../components/AudioPlayer";
import { LeafBranch, SmallSprig } from "../components/Botanical";
import {
  Check, Star, ArrowRight, ShieldCheck, Quote, Headphones,
  BookOpen, HeartHandshake, Sparkles, QrCode, ChevronDown,
  ScanLine, Smartphone, PlayCircle, BadgeCheck, Share2,
} from "lucide-react";

const COVER = `${process.env.PUBLIC_URL || ""}/assets/ebook-cover.jpg`;

const PREVIEW_PAGES = [
  { src: "/assets/preview-sumario.jpg", label: "Sumário" },
  { src: "/assets/preview-introducao.jpg", label: "Introdução" },
  { src: "/assets/preview-como-usar.jpg", label: "Como usar" },
  { src: "/assets/preview-capitulo.jpg", label: "Capítulos" },
  { src: "/assets/preview-exercicio.jpg", label: "Exercícios" },
];

const STEPS = [
  { icon: ScanLine, title: "1. Escaneie o QR Code", text: "Aponte a câmera do celular para o QR Code que acompanha cada oração no e-book." },
  { icon: Smartphone, title: "2. Abra a página", text: "Uma página simples e bonita abre no seu navegador, sem instalar nada." },
  { icon: PlayCircle, title: "3. Ouça a oração", text: "Toque no play, respire fundo e viva o seu momento com Deus, onde estiver." },
];

const SAMPLE_TITLES = [
  "Quando eu não souber o que dizer", "Para começar o dia", "Antes de dormir",
  "Quando eu estiver ansiosa", "Pela minha família", "Pelos meus filhos",
  "Quando eu pensar em desistir", "Para pedir perdão", "Quando eu estiver com medo",
  "Para agradecer", "Quando eu precisar de paz", "Quando eu só quiser conversar com Deus",
];

const LIFESTYLE = "https://images.unsplash.com/photo-1475938476802-32a7e851dad1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const MOMENTS = [
  "Quando a ansiedade aperta o peito",
  "Antes de dormir ou ao amanhecer",
  "Pela família e por quem você ama",
  "Quando você pensa em desistir",
  "Para agradecer as bênçãos",
  "Quando falta fé e falta paz",
];

const BENEFITS = [
  { icon: BookOpen, title: "50 orações guiadas", text: "Uma para cada momento da vida — nunca mais fique sem saber o que dizer." },
  { icon: Headphones, title: "Áudios narrados", text: "Ouça cada oração com calma pelo celular, no seu tempo, onde estiver." },
  { icon: HeartHandshake, title: "Feito para recomeçar", text: "Exercícios curtos e um checklist para tornar a oração um hábito leve." },
  { icon: Sparkles, title: "Mais fé, mais paz", text: "Um passo de cada dia para se aproximar de Deus, todos os dias." },
];

const TESTIMONIALS = [
  { name: "Mariana A.", text: "Eu queria orar, mas sempre travava. Agora abro uma oração, respiro e as palavras vêm. Mudou minhas manhãs." },
  { name: "Cláudia R.", text: "Os áudios me acompanham antes de dormir. É como ter uma companhia serena me levando até Deus." },
  { name: "Patrícia L.", text: "Comprei num momento difícil e foi um abraço. Hoje a oração faz parte da minha rotina — leve e verdadeira." },
];

const FAQ = [
  { q: "Como recebo o e-book?", a: "O acesso é digital e imediato após a compra. Você recebe o e-book completo com as 50 orações e os áudios." },
  { q: "Preciso de algum aplicativo especial?", a: "Não. Basta o seu celular. Cada oração tem um QR Code que abre uma página simples para ouvir o áudio." },
  { q: "As orações servem para qualquer momento?", a: "Sim. São 50 orações para diferentes situações — ansiedade, gratidão, família, recomeços e muito mais." },
];

function BuyButton({ checkoutUrl, children, testid, className = "" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full bg-[#3A2921] text-[#FDFBF7] font-semibold tracking-wide hover:bg-[#5C4638] transition-all transform hover:scale-[1.03] border border-[#C5A059]/50 shadow-md";
  if (checkoutUrl) {
    return (
      <a href={checkoutUrl} target="_blank" rel="noreferrer" data-testid={testid} className={`${base} ${className}`}>
        {children}
      </a>
    );
  }
  return (
    <button
      data-testid={testid}
      onClick={() => toast("Link de compra em breve. Volte em instantes! 🙏", { description: "Estamos finalizando o checkout." })}
      className={`${base} ${className}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return (
    <div className="flex items-center justify-center gap-4 py-2">
      <span className="h-px w-16 bg-[#D8CBB8]" />
      <span className="font-cinzel text-[10px] sm:text-xs tracking-[0.3em] text-[#9C877A] uppercase">
        Mais Fé · Mais Paz · Todos os Dias
      </span>
      <span className="h-px w-16 bg-[#D8CBB8]" />
    </div>
  );
}

export default function Landing() {
  const [config, setConfig] = useState({ checkout_url: "", price: "R$ 29,90", offer_deadline: "" });
  const [prayer, setPrayer] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [checkoutUrl, setCheckoutUrl] = useState(getCheckoutUrl());
  const visitTracked = useRef(false);
  const revealRef = useRef(null);

  useEffect(() => {
    const root = revealRef.current;
    if (!root) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;
    const sections = Array.from(root.querySelectorAll("section")).slice(1); // pula o herói
    sections.forEach((s) => s.classList.add("reveal"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    captureTracking();
    setCheckoutUrl(getCheckoutUrl());
    if (!visitTracked.current) {
      visitTracked.current = true;
      api.post("/track/visit", { origem: getOrigem() }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    api.get("/config").then((res) => setConfig(res.data)).catch(() => {});
    api.get("/prayers/1").then((res) => setPrayer(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsSample = params.get("ir") === "oracao" || window.location.hash === "#oracao-amostra";
    if (!wantsSample) return;

    // Evita que o navegador (ou o webview do WhatsApp) restaure a rolagem para o topo.
    if ("scrollRestoration" in window.history) {
      try { window.history.scrollRestoration = "manual"; } catch (e) {}
    }

    let userInteracted = false;
    const markInteracted = () => { userInteracted = true; };
    const interactionEvents = ["wheel", "touchstart", "keydown", "mousedown"];
    interactionEvents.forEach((ev) =>
      window.addEventListener(ev, markInteracted, { passive: true })
    );

    const scrollToSample = () => {
      if (userInteracted) return;
      const el = document.getElementById("oracao-amostra");
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };

    // Reforça a posição repetidamente (imagens deslocam o layout e o webview reseta o scroll).
    const start = Date.now();
    const interval = setInterval(() => {
      scrollToSample();
      if (userInteracted || Date.now() - start > 6000) clearInterval(interval);
    }, 200);
    const onLoad = () => scrollToSample();
    window.addEventListener("load", onLoad);

    return () => {
      clearInterval(interval);
      window.removeEventListener("load", onLoad);
      interactionEvents.forEach((ev) => window.removeEventListener(ev, markInteracted));
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const deadline = config.offer_deadline ? new Date(config.offer_deadline).getTime() : null;
  const remaining = deadline ? deadline - now : null;
  const showTimer = remaining !== null && remaining > 0;
  const cd = showTimer
    ? {
        d: Math.floor(remaining / 86400000),
        h: Math.floor((remaining % 86400000) / 3600000),
        m: Math.floor((remaining % 3600000) / 60000),
        s: Math.floor((remaining % 60000) / 1000),
      }
    : null;
  const pad = (n) => String(n).padStart(2, "0");

  const CountdownBox = () => (
    <div data-testid="offer-countdown" className="flex items-center justify-center gap-2 mt-4">
      {[["Dias", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Seg", cd.s]].map(([label, val], i) => (
        <div key={i} className="bg-[#3A2921] text-[#FDFBF7] rounded-xl px-3 py-2 min-w-[54px] text-center border border-[#C5A059]/40">
          <span className="block font-serif-display text-2xl font-semibold leading-none">{pad(val)}</span>
          <span className="block text-[9px] uppercase tracking-widest text-[#C5A059] mt-1">{label}</span>
        </div>
      ))}
    </div>
  );

  const audioSrc = `${api.defaults.baseURL}/prayers/1/audio`;
  const qrSrc = `${api.defaults.baseURL}/qrcodes/oracao/1.png`;

  return (
    <div ref={revealRef} className="relative overflow-hidden bg-[#FDFBF7]">
      {/* ===== HERO ===== */}
      <section className="relative">
        <div className="sun-rays pointer-events-none" aria-hidden="true" />
        <div className="sun-glow" aria-hidden="true" />
        <LeafBranch className="pointer-events-none absolute -top-6 -left-8 w-28 sm:w-40 opacity-25 z-10" />
        <LeafBranch flip className="pointer-events-none absolute top-24 -right-8 w-28 sm:w-40 opacity-25 z-10" />

        <div className="max-w-6xl mx-auto px-5 pt-10 pb-16 sm:pt-14 sm:pb-20 relative z-10">
          <div className="text-center mb-10 fade-up">
            <span className="font-cinzel uppercase tracking-[0.28em] text-[#C5A059] text-[11px] sm:text-xs">
              E-book Devocional
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            {/* Cover */}
            <div className="order-1 md:order-none relative flex justify-center fade-up">
              {/* Opção B — ramo decorativo à esquerda (espelha o lado direito) */}
              <LeafBranch className="hidden md:block pointer-events-none absolute -left-16 lg:-left-24 top-1/2 -translate-y-1/2 w-40 lg:w-56 opacity-40 z-0 rotate-[8deg]" />
              <SmallSprig className="hidden md:block pointer-events-none absolute -left-6 bottom-2 w-16 opacity-40 z-0" />
              <div className="relative z-10">
                <div className="absolute -inset-4 bg-[#EADFCB] rounded-[2rem] blur-2xl opacity-60" />
                <img
                  src={COVER}
                  alt="Capa do e-book Quando Faltam Palavras"
                  data-testid="ebook-cover-image"
                  className="relative w-64 sm:w-80 rounded-2xl shadow-2xl ring-1 ring-[#E5DCCF] rotate-[-2deg] hover:rotate-0 transition-transform duration-500"
                />

                {/* Selo redondo: 50 orações em áudio */}
                <div
                  data-testid="hero-badge-audio"
                  className="absolute -top-5 -left-6 sm:-top-7 sm:-left-12 z-20 w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#3A2921] text-[#F7EFE0] shadow-xl border-2 border-[#C5A059]/60 flex flex-col items-center justify-center text-center rotate-[-10deg] animate-[float_5s_ease-in-out_infinite]"
                >
                  <Headphones className="w-6 h-6 sm:w-7 sm:h-7 text-[#D8B36A]" strokeWidth={1.6} />
                  <span className="font-cinzel font-bold text-lg sm:text-xl leading-none mt-1">50</span>
                  <span className="text-[8px] sm:text-[9px] uppercase tracking-widest text-[#EADFCB] mt-0.5 px-1 leading-tight">
                    orações<br />em áudio
                  </span>
                </div>

                {/* Etiqueta: Acesso imediato */}
                <div
                  data-testid="hero-badge-instant"
                  className="absolute -bottom-4 -left-4 sm:-bottom-5 sm:-left-10 z-20 inline-flex items-center gap-1.5 rounded-full bg-[#F7F2E9] text-[#3A2921] shadow-lg border border-[#E5DCCF] px-3.5 py-2 rotate-[4deg]"
                >
                  <BadgeCheck className="w-4 h-4 text-[#4C7A5A]" />
                  <span className="text-xs font-semibold tracking-wide">Acesso imediato</span>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="text-center md:text-left fade-up delay-1">
              <h1 className="font-serif-display text-[#3A2921] font-semibold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
                Quando Faltam<br />Palavras
              </h1>
              <p className="font-cinzel uppercase tracking-[0.22em] text-[#8C7565] text-xs sm:text-sm mt-4">
                50 Orações para Falar com Deus
              </p>
              <p className="font-serif-display italic text-[#6E5A4F] text-lg sm:text-xl mt-6 leading-relaxed">
                Um guia para quem quer orar, mas nem sempre sabe o que dizer.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center md:items-start gap-4">
                <BuyButton checkoutUrl={checkoutUrl} testid="landing-buy-button-hero" className="px-8 py-4 text-base w-full sm:w-auto">
                  Quero meu exemplar <ArrowRight className="w-4 h-4" />
                </BuyButton>
                <div className="text-center sm:text-left">
                  <span data-testid="launch-offer-badge" className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#8A5A1E] bg-[#F3E2C6] border border-[#E4CB9C] rounded-full px-3 py-1 mb-1">
                    ★ Oferta de lançamento
                  </span>
                  <span data-testid="landing-price-hero" className="block font-serif-display text-2xl text-[#3A2921] font-semibold">
                    {config.price}
                  </span>
                </div>
              </div>

              <p data-testid="trust-microcopy-hero" className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-x-2 gap-y-1 text-xs text-[#8C7565]">
                <ShieldCheck className="w-3.5 h-3.5 text-[#4C7A5A]" />
                <span>Acesso imediato</span><span className="text-[#D8CBB8]">•</span>
                <span>Pagamento seguro</span><span className="text-[#D8CBB8]">•</span>
                <span>Pix, cartão ou boleto</span><span className="text-[#D8CBB8]">•</span>
                <span>Garantia de 7 dias</span>
              </p>

              <div className="mt-6 flex items-center justify-center md:justify-start gap-1 text-[#C5A059]">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" strokeWidth={0} />)}
                <span className="text-xs text-[#9C877A] ml-2">amado por milhares de homens e mulheres com fé</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== RIBBON ===== */}
      <div className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-4">
        <Divider />
      </div>

      {/* ===== PAIN / FOR WHOM ===== */}
      <section className="max-w-4xl mx-auto px-5 py-16 sm:py-20 text-center">
        <SmallSprig className="w-14 opacity-50 mx-auto mb-6" />
        <h2 className="font-serif-display italic text-3xl sm:text-4xl text-[#3A2921] font-semibold leading-tight">
          "Deus não espera palavras perfeitas.<br className="hidden sm:block" /> Ele espera apenas um coração disposto a conversar com Ele."
        </h2>
        <p className="text-[#6E5A4F] text-base sm:text-lg mt-6 leading-relaxed max-w-2xl mx-auto">
          Você não precisa encontrar as palavras perfeitas para falar com Deus.
          Este guia reúne <strong className="text-[#3A2921]">50 orações</strong> para os
          diferentes momentos da vida — ajudando você a começar, criar constância e
          tornar a oração parte dos seus dias.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mt-10 text-left">
          {MOMENTS.map((m, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#FAF7F2] border border-[#E5DCCF] rounded-xl px-4 py-3">
              <span className="w-7 h-7 rounded-full bg-[#C5A059]/15 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4 text-[#C5A059]" />
              </span>
              <span className="font-serif-display text-lg text-[#3A2921]">{m}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold text-center mb-12">
            O que você recebe
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-[#FDFBF7] rounded-2xl border border-[#E5DCCF] p-6 text-center hover:shadow-md transition-shadow">
                <span className="inline-flex w-12 h-12 rounded-full bg-[#3A2921] items-center justify-center mb-4 border border-[#C5A059]/40">
                  <b.icon className="w-5 h-5 text-[#FDFBF7]" strokeWidth={1.6} />
                </span>
                <h3 className="font-serif-display text-xl text-[#3A2921] mb-2">{b.title}</h3>
                <p className="text-sm text-[#6E5A4F] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <div className="text-center mb-12">
          <span className="font-cinzel uppercase tracking-[0.25em] text-[#C5A059] text-xs">Simples assim</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold mt-3">
            Como funciona
          </h2>
          <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">
            Cada oração do e-book tem um QR Code que abre o áudio narrado no seu celular.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 relative">
          {STEPS.map((s, i) => (
            <div key={i} className="text-center px-4">
              <span className="inline-flex w-16 h-16 rounded-full bg-[#F4EFE6] border border-[#E5DCCF] items-center justify-center mb-4">
                <s.icon className="w-7 h-7 text-[#C5A059]" strokeWidth={1.5} />
              </span>
              <h3 className="font-serif-display text-xl text-[#3A2921] mb-2">{s.title}</h3>
              <p className="text-sm text-[#6E5A4F] leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== EBOOK PREVIEW ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-10">
            <span className="font-cinzel uppercase tracking-[0.25em] text-[#C5A059] text-xs">Espie por dentro</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold mt-3">
              Um livro feito com cuidado, do começo ao fim
            </h2>
            <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">
              Diagramação editorial, elegante e acolhedora — pensada para tornar a oração um hábito leve.
            </p>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x sm:justify-center">
            {PREVIEW_PAGES.map((p, i) => (
              <figure key={i} className="shrink-0 snap-center" data-testid={`preview-page-${i}`}>
                <img
                  src={p.src}
                  alt={`Página: ${p.label}`}
                  className="w-44 sm:w-48 rounded-xl border border-[#E5DCCF] shadow-md bg-white hover:-translate-y-1 transition-transform duration-300"
                />
                <figcaption className="text-center text-xs uppercase tracking-widest text-[#9C877A] mt-3">{p.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SAMPLE PRAYER ===== */}
      <section id="oracao-amostra" className="scroll-mt-20 max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <div className="text-center mb-10">
          <span className="font-cinzel uppercase tracking-[0.25em] text-[#C5A059] text-xs">Experimente agora</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold mt-3">
            Ouça uma oração, por nossa conta
          </h2>
          <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">
            Um gostinho do que espera por você. Respire fundo — este é o seu momento com Deus.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8 items-center bg-[#FAF7F2] rounded-3xl border border-[#E5DCCF] p-6 sm:p-9 relative overflow-hidden">
          <SmallSprig className="absolute top-5 right-5 w-16 opacity-30" />
          <div className="md:col-span-3">
            <span className="font-cinzel uppercase tracking-[0.25em] text-[#C5A059] text-xs font-semibold">
              Oração 01
            </span>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3A2921] font-semibold mt-2 mb-5">
              {prayer?.title || "Quando eu não souber o que dizer"}
            </h3>
            <AudioPlayer src={audioSrc} hasAudio={!!prayer?.has_audio} />
          </div>

          <div className="md:col-span-2 flex flex-col items-center text-center">
            <img
              src={qrSrc}
              alt="QR Code da oração de amostra"
              data-testid="landing-sample-qr"
              className="w-40 h-40 rounded-xl border border-[#E5DCCF] bg-white p-2"
            />
            <p className="flex items-center gap-2 text-sm text-[#6E5A4F] mt-4">
              <QrCode className="w-4 h-4 text-[#C5A059]" />
              Aponte a câmera para ouvir no celular
            </p>
            <button
              data-testid="share-whatsapp-button"
              onClick={() => {
                const base = (config.public_base_url || window.location.origin).replace(/\/$/, "");
                const msg = `Ouça esta oração 🙏 "${prayer?.title || "Quando eu não souber o que dizer"}" — e conheça o e-book "Quando Faltam Palavras", com 50 orações para falar com Deus: ${base}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:brightness-95 transition-all"
            >
              <Share2 className="w-4 h-4" /> Enviar oração para alguém
            </button>
          </div>
        </div>
      </section>

      {/* ===== SAMPLE TITLES ===== */}
      <section className="max-w-4xl mx-auto px-5 pb-4 sm:pb-8">
        <div className="text-center mb-8">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold">
            Uma oração para cada momento
          </h2>
          <p className="text-[#6E5A4F] mt-3">Veja algumas das 50 orações que esperam por você:</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2.5">
          {SAMPLE_TITLES.map((t, i) => (
            <span key={i} className="bg-[#FAF7F2] border border-[#E5DCCF] rounded-full px-4 py-2 text-sm font-serif-display text-[#4A362B]">
              {t}
            </span>
          ))}
          <span className="bg-[#3A2921] text-[#F3E9DB] rounded-full px-4 py-2 text-sm font-semibold">
            …e mais 38 orações
          </span>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A2921] font-semibold text-center mb-12">
            Palavras de quem já vive isso
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#FDFBF7] rounded-2xl border border-[#E5DCCF] p-6 flex flex-col">
                <Quote className="w-7 h-7 text-[#C5A059] mb-3" />
                <p className="font-serif-display italic text-[#4A362B] text-lg leading-relaxed flex-1">"{t.text}"</p>
                <div className="mt-5 flex items-center gap-2">
                  <div className="flex text-[#C5A059]">
                    {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <span className="text-sm font-semibold text-[#6E5A4F]">— {t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LIFESTYLE BAND ===== */}
      <section className="relative">
        <div
          className="h-72 sm:h-96 bg-cover bg-center flex items-center justify-center"
          style={{ backgroundImage: `linear-gradient(rgba(58,41,33,0.55), rgba(58,41,33,0.55)), url(${LIFESTYLE})` }}
        >
          <div className="text-center px-6 max-w-2xl">
            <SmallSprig className="w-14 opacity-70 mx-auto mb-5" />
            <p className="font-serif-display italic text-2xl sm:text-3xl text-[#FDFBF7] leading-relaxed">
              "Você não precisa das palavras perfeitas.<br className="hidden sm:block" /> Só precisa começar."
            </p>
          </div>
        </div>
      </section>

      {/* ===== OFFER ===== */}
      <section className="max-w-3xl mx-auto px-5 py-16 sm:py-24 text-center">
        <LeafBranch className="pointer-events-none absolute left-2 w-24 opacity-20 hidden sm:block" />
        <div className="bg-[#3A2921] text-[#FDFBF7] rounded-[2rem] p-8 sm:p-12 relative overflow-hidden border border-[#C5A059]/30">
          <SmallSprig className="w-16 opacity-40 mx-auto mb-6" />
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">
            Comece hoje a sua vida de oração
          </h2>
          <p className="text-[#E7D9C6] mt-4 max-w-lg mx-auto">
            Leve com você as 50 orações e os áudios. Uma vida de oração também se constrói um passo de cada vez.
          </p>

          <div data-testid="what-you-get" className="text-left max-w-sm mx-auto mt-8 bg-[#FDFBF7]/[0.06] border border-[#C5A059]/25 rounded-2xl p-5">
            <div className="font-cinzel uppercase tracking-[0.22em] text-[11px] text-[#C5A059] mb-4 text-center">O que você recebe</div>
            <ul className="space-y-3">
              {[
                "E-book “Quando Faltam Palavras”",
                "Método prático de oração",
                "Exercícios guiados",
                "50 orações em áudio (por QR Code)",
                "Acesso digital imediato",
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="inline-flex w-6 h-6 rounded-full bg-[#C5A059]/20 items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={3} />
                  </span>
                  <span className="text-[#F3E9DB]">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <span data-testid="offer-badge" className="inline-block text-[11px] font-bold uppercase tracking-[0.25em] text-[#2A1D16] bg-[#C5A059] rounded-full px-4 py-1.5">
              ★ Oferta de Lançamento
            </span>
            <span data-testid="landing-price" className="font-serif-display text-5xl font-semibold block mt-3">{config.price}</span>
            <span className="text-sm text-[#C9B79F]">pagamento único · acesso imediato</span>
            {showTimer && <CountdownBox />}
            {showTimer && (
              <p className="text-xs text-[#C9B79F] mt-3">Esta condição especial termina em breve.</p>
            )}
          </div>

          <BuyButton checkoutUrl={checkoutUrl} testid="landing-buy-button" className="mt-8 px-10 py-4 text-lg bg-[#C5A059] hover:bg-[#B08B44] text-[#2A1D16] border-none">
            Quero meu exemplar <ArrowRight className="w-5 h-5" />
          </BuyButton>

          <div className="mt-6 flex items-center justify-center gap-3 bg-[#FDFBF7]/10 border border-[#C5A059]/30 rounded-2xl px-5 py-4 max-w-sm mx-auto">
            <BadgeCheck className="w-8 h-8 text-[#C5A059] shrink-0" strokeWidth={1.6} />
            <p className="text-left text-sm text-[#F3E9DB]">
              <strong className="text-[#FDFBF7]">7 dias de garantia.</strong> Se não fizer sentido para você, devolvemos 100% do valor.
            </p>
          </div>

          <p className="flex items-center justify-center gap-2 text-xs text-[#C9B79F] mt-5">
            <ShieldCheck className="w-4 h-4" /> Compra segura · Acesso imediato · Pix, cartão ou boleto
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="max-w-2xl mx-auto px-5 pb-20">
        <h2 className="font-serif-display text-3xl text-[#3A2921] font-semibold text-center mb-8">Perguntas frequentes</h2>
        <div className="flex flex-col gap-3">
          {FAQ.map((item, i) => (
            <div key={i} className="bg-[#FAF7F2] border border-[#E5DCCF] rounded-xl overflow-hidden">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-serif-display text-lg text-[#3A2921]">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#C5A059] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-4 text-[#6E5A4F] text-sm leading-relaxed">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#F4EFE6] border-t border-[#E5DCCF] py-10 text-center">
        <h3 className="font-cinzel uppercase tracking-[0.22em] text-[#3A2921] text-sm">Quando Faltam Palavras</h3>
        <p className="font-serif-display italic text-[#9C877A] mt-1">50 Orações para Falar com Deus</p>
        <p className="text-sm text-[#6E5A4F] mt-4">por <span className="font-semibold text-[#3A2921]">Felipe Ramalho</span></p>
        <div className="mt-4"><Divider /></div>
        <Link to="/admin" className="inline-block mt-4 text-xs text-[#B7A896] hover:text-[#6E5A4F] transition-colors">
          Área administrativa
        </Link>
      </footer>

      {/* spacer for sticky mobile bar */}
      <div className="h-20 sm:hidden" />

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-[#FDFBF7]/95 backdrop-blur border-t border-[#E5DCCF] px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_20px_rgba(58,41,33,0.08)]">
        <div className="leading-tight">
          <span className="block text-[10px] uppercase tracking-widest text-[#9C877A]">Acesso imediato</span>
          <span className="font-serif-display text-xl text-[#3A2921] font-semibold">{config.price}</span>
        </div>
        <BuyButton checkoutUrl={checkoutUrl} testid="landing-buy-button-sticky" className="px-6 py-3 text-sm flex-1 max-w-[220px]">
          Quero meu exemplar <ArrowRight className="w-4 h-4" />
        </BuyButton>
      </div>
    </div>
  );
}
